/**
 * Seitenzahl eines PDF für die Dokumentkarte.
 * Bytes über den bestehenden Klartext-Abruf (dieselbe Anmeldung, Nextcloud),
 * im Browser nur gezählt. Kein Seitenbild, kein externer Vorschaudienst.
 * DOCX bleibt außen: hier gibt es keinen bestehenden Seitenweg.
 */
import {
  PORTAL_PLAINTEXT_FETCH_URL,
  hasPortalAuthzToken,
  portalAuthzHeaders,
  resolveOnlyofficeIssueJwtUrl,
} from '../../constants';

type PdfDocument = {
  numPages: number;
  destroy(): Promise<void>;
};
type PdfJs = {
  getDocument(src: { data: Uint8Array }): { promise: Promise<PdfDocument> };
  GlobalWorkerOptions: { workerSrc: string };
};

const cache = new Map<string, number | null>();
const inflight = new Map<string, Promise<number | null>>();

function cacheKey(documentUrl: string, nextcloudPath?: string): string {
  return `${nextcloudPath?.trim() || ''}|${documentUrl.trim()}`;
}

function looksLikePdf(bytes: Uint8Array): boolean {
  if (bytes.byteLength < 5) return false;
  return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
}

function base64ToBytes(b64: string): Uint8Array | null {
  try {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

async function loadPdfBytes(documentUrl: string, nextcloudPath?: string): Promise<Uint8Array | null> {
  if (!PORTAL_PLAINTEXT_FETCH_URL || !hasPortalAuthzToken()) return null;
  const url = resolveOnlyofficeIssueJwtUrl(PORTAL_PLAINTEXT_FETCH_URL);
  const body: Record<string, string> = {};
  if (documentUrl.trim()) body.document_url = documentUrl.trim();
  if (nextcloudPath?.trim()) body.nextcloud_path = nextcloudPath.trim();
  if (!body.document_url && !body.nextcloud_path) return null;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...portalAuthzHeaders(url),
    },
    credentials: 'omit',
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { ok?: boolean; text_utf8_base64?: string };
  if (!data.ok || typeof data.text_utf8_base64 !== 'string' || !data.text_utf8_base64) return null;
  const bytes = base64ToBytes(data.text_utf8_base64);
  if (!bytes || !looksLikePdf(bytes)) return null;
  return bytes;
}

async function readPageCount(bytes: Uint8Array): Promise<number | null> {
  const pdfjs = (await import('./vendor/pdfjs/pdf.min.mjs')) as PdfJs;
  pdfjs.GlobalWorkerOptions.workerSrc = new URL('./vendor/pdfjs/pdf.worker.min.mjs', import.meta.url).toString();
  const pdf = await pdfjs.getDocument({ data: bytes }).promise;
  try {
    const n = pdf.numPages;
    return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
  } finally {
    await pdf.destroy().catch(() => undefined);
  }
}

/** Seitenzahl, oder null — dann fehlt die Zeile, es wird nichts gezeichnet. */
export async function countPortalPdfPages(input: {
  documentUrl?: string | null;
  nextcloudPath?: string | null;
}): Promise<number | null> {
  const documentUrl = input.documentUrl?.trim() || '';
  const nextcloudPath = input.nextcloudPath?.trim() || '';
  if (!documentUrl && !nextcloudPath) return null;
  const key = cacheKey(documentUrl, nextcloudPath);
  if (cache.has(key)) return cache.get(key) ?? null;
  const pending = inflight.get(key);
  if (pending) return pending;
  const job = (async () => {
    try {
      const bytes = await loadPdfBytes(documentUrl, nextcloudPath);
      if (!bytes) return null;
      return await readPageCount(bytes);
    } catch {
      return null;
    }
  })().then((count) => {
    cache.set(key, count);
    inflight.delete(key);
    return count;
  });
  inflight.set(key, job);
  return job;
}
